import type { ExistingAttachment } from '../../../../components/AttachmentManager'
import ApiService from '../../../../services/ApiService'
import AttachmentApiService from '../../../../services/AttachmentApiService'
import type { FashionOrderFormValues, FashionOrderInstallment } from './fashionOrder.schema'
import { buildFashionItemsPayload } from './fashionItems.payload'
import type { ProductApiData } from './types'
import { centsDigitsToReais, cleanCurrency, formatDate, normalizeProductData } from './utils'

export type LoadedFormValues = Omit<FashionOrderFormValues, 'paymentMethodId' | 'installments'>

export interface LoadedFashionOrder {
  values: LoadedFormValues
  products: ProductApiData[]
  attachments: ExistingAttachment[]
  kitCount: number
  paymentMethodId: number | null
  installments: FashionOrderInstallment[]
}

/** Os `try/catch` dos GETs só logam: pedido com vendedor ou empresa excluída ainda precisa abrir. */
export const fetchFashionOrderById = async (orderId: number): Promise<LoadedFashionOrder> => {
  const { data } = await ApiService.steelOrders.getOrder(orderId)

  let sellerObj = {
    id: data.sellerId || 0,
    name: data.sellerDetails?.name || data.seller || '',
    email: data.sellerDetails?.email || '',
    Profile: data.sellerDetails?.Profile || null,
    profile: data.sellerDetails?.profile || null,
    profileId: data.sellerDetails?.profileId || null,
  }

  if (data.sellerId) {
    try {
      const sellerRes = await ApiService.user.findById(data.sellerId)
      if (sellerRes.data) {
        sellerObj = {
          id: data.sellerId,
          name: sellerRes.data.name || sellerObj.name,
          email: sellerRes.data.email || sellerObj.email,
          Profile: sellerRes.data.Profile || sellerRes.data.profile || sellerObj.Profile,
          profile: sellerRes.data.Profile || sellerRes.data.profile || sellerObj.profile,
          profileId: sellerRes.data.profileId || sellerObj.profileId,
        }
      }
    } catch (e) {
      console.warn(`Não foi possível buscar vendedor ${data.sellerId} durante edição:`, e)
    }
  }

  let supplierObj = { id: Number(data.supplierId), companyName: data.supplierDetails?.name || data.supplier || '' }
  if (data.supplierId) {
    try {
      const supplierRes = await ApiService.companies.findById(data.supplierId)
      if (supplierRes.data) {
        supplierObj = supplierRes.data
      }
    } catch (e) {
      console.warn(`Não foi possível buscar fornecedor ${data.supplierId} durante edição:`, e)
    }
  }

  let companyObj = { id: Number(data.clientId), companyName: data.clientDetails?.name || data.client || '' }
  if (data.clientId) {
    try {
      const companyRes = await ApiService.companies.findById(data.clientId)
      if (companyRes.data) {
        companyObj = companyRes.data
      }
    } catch (e) {
      console.warn(`Não foi possível buscar cliente ${data.clientId} durante edição:`, e)
    }
  }

  const values: LoadedFormValues = {
    supplier: supplierObj.companyName || '',
    supplierId: supplierObj.id || undefined,
    supplierDetails: supplierObj,
    seller: sellerObj.name || '',
    sellerId: sellerObj.id || undefined,
    sellerDetails: sellerObj,
    client: companyObj.companyName || '',
    clientId: companyObj.id || undefined,
    clientDetails: companyObj,
    carrier: data.carrier ? { id: Number(data.carrier.id), companyName: data.carrier.companyName || '' } : { id: 0, companyName: '' },
    orderType: data.orderType?.name || '',
    orderTypeId: data.orderTypeId || undefined,
    representativeId: data.representativeId || undefined,
    representativeName: data.representative?.name || '',
    orderSituationId: data.orderSituationId || undefined,
    orderSituationName: data.orderSituation?.name || '',
    generalClassId: data.generalClassId || undefined,
    generalClassName: data.generalClass?.name || '',
    billingDate: data.billingDate ? formatDate(data.billingDate) : '',
    sellingPrice: data.sellingPrice != null
      ? Number(data.sellingPrice)
      : (data.total != null && Number(data.total) > 0 ? Number(data.total) : undefined),
    paymentConditionId: typeof data.paymentCondition === 'object' && data.paymentCondition !== null
      ? (data.paymentCondition as { id?: number }).id
      : undefined,
    paymentConditionName: typeof data.paymentCondition === 'object' && data.paymentCondition !== null
      ? String((data.paymentCondition as { name?: string }).name || '')
      : String(data.paymentTerm || ''),
    shippingTypeId: typeof data.shippingType === 'object' && data.shippingType !== null
      ? (data.shippingType as { id?: number }).id
      : undefined,
    shippingTypeName: typeof data.shippingType === 'object' && data.shippingType !== null
      ? String((data.shippingType as { name?: string }).name || '')
      : String(data.shippingType || ''),
    trackingCode: data.trackingCode || '',
    orderDate: formatDate(data.orderDate) || new Date().toISOString().split('T')[0],
    deliveryDate: formatDate(data.finalDateOfDelivery || data.startDateOfDelivery),
    criticalAnalysis: data.criticalAnalysis || false,
    criticalAnalysisDesc: data.criticalAnalysisDesc || '',
    extraInfo: data.extraInfo || data.extraInformation || '',
    quantityItems: data.quantityItems || '0',
    quantityPieces: data.quantityPieces || '0',
    totalCost: `R$ ${Number(data.totalCost || 0).toFixed(2).replace('.', ',')}`,
    deliveryTime: data.deliveryTime || '',
    shippingCost:
      data.shippingValue != null && data.shippingValue !== ''
        ? String(Math.round(Number(data.shippingValue) * 100))
        : data.shippingCost != null && data.shippingCost !== ''
          ? String(Math.round(Number(data.shippingCost) * 100))
          : '',
    maximumBillingAmount: data.maximumBillingAmount || '',
  }

  const attachments: ExistingAttachment[] = (data.attachments || []).map((a: any) => ({
    id: a.id,
    originalName: a.originalName || a.title || a.fileName || 'Arquivo',
    filePath: a.fileUrl || a.filePath,
    fileSize: a.fileSize,
    mimeType: a.mimeType,
  }))

  const products: ProductApiData[] = (data.items || [])
    .filter((item: any) => item.itemType === 'PRODUCT')
    .map((item: any) => {
      let extra: any = {}
      try { extra = item.extraInfo ? JSON.parse(item.extraInfo) : {} } catch { /* ignore */ }

      const P = item.Product as { Family?: { id?: number; name?: string }; family?: { id?: number; name?: string } } | undefined
      const famRel = P?.Family || P?.family
      const famId = Number(extra.familyId ?? famRel?.id ?? 0)
      const famName = String(extra.familyName || famRel?.name || '').trim()

      const unitPrice = Number(extra.fashionProductPrice || item.Product?.price || 0)
      const totalPieces = Number(item.quantity) || 0
      const rawItemPrice = Number(item.itemPrice ?? item.priceKg) || 0
      const subtotal = rawItemPrice > 0 ? rawItemPrice : totalPieces * unitPrice

      return normalizeProductData({
        id: item.id,
        fashionProductId: item.productId,
        fashionProduct: {
          id: item.productId,
          name: extra.fashionProductName || item.Product?.name || '',
          price: unitPrice,
          ncmId: item.Product?.ncmId ?? null,
          group: {
            id: extra.groupId || item.Product?.group?.id,
            name: extra.groupName || item.Product?.group?.name || '',
            family: {
              id: famId,
              name: famName,
              collectionId: extra.collectionId,
            },
          },
        },
        family: famName ? { id: famId, name: famName } : undefined,
        discount: item.descount || 0,
        sellingPrice: item.sellingPrice || null,
        totalItems: totalPieces,
        subtotal,
        items: extra.fashionItems || [],
        colorCount: extra.colorCount || 0,
        colors: extra.colors || [],
        selectedColors: extra.selectedColors || [],
        kitIndex: item.kitIndex !== undefined ? item.kitIndex : null,
        nomeCustomizado: typeof item.nomeCustomizado === 'string' ? item.nomeCustomizado : undefined,
      })
    })

  const maxKitIndex = products.reduce((max: number, p: ProductApiData) => {
    if (p.kitIndex !== null && p.kitIndex !== undefined && p.kitIndex > max) return p.kitIndex
    return max
  }, -1)

  const pmRelation = data.OrderPaymentMethod?.[0] || data.orderPaymentMethod?.[0] || data.paymentMethods?.[0] || data.orderPaymentMethods?.[0];
  let paymentMethodId: number | null = null;
  if (pmRelation) {
    const pmId = pmRelation.paymentMethodId || pmRelation.id || pmRelation.paymentMethod?.id || pmRelation.PaymentMethod?.id;
    if (pmId) {
      paymentMethodId = Number(pmId);
    }
  }

  const installments = (data.OrderInstallment || []).map((inst: any) => ({
    id: inst.id,
    numero: inst.numero,
    valor: Number(inst.valor || 0),
    vencimento: formatDate(inst.vencimento),
    diasOffset: inst.diasOffset ?? 0,
    comissaoAtiva: Boolean(inst.comissaoAtiva),
  }));

  return {
    values,
    products,
    attachments,
    kitCount: maxKitIndex >= 0 ? maxKitIndex + 1 : 0,
    paymentMethodId,
    installments,
  }
}

export const buildOrderPayload = (
  values: FashionOrderFormValues,
  items: ProductApiData[],
  fileIds: number[],
) => {
  const toDate = (d?: string | null) => d ? `${d}T00:00:00.000Z` : undefined
  const deliveryDateVal = toDate(values.deliveryDate)

  const payload: any = {
    clientId: values.clientId || undefined,
    supplierId: values.supplierId || undefined,
    sellerId: values.sellerId || undefined,
    carrierId: values.carrier?.id || undefined,
    orderTypeId: values.orderTypeId || undefined,
    orderType: values.orderType || 'Standard',
    billingDate: values.billingDate ? `${values.billingDate}T00:00:00.000Z` : undefined,
    clientOrderNumber: values.clientOrderNumber ?? undefined,
    representativeId: values.representativeId || undefined,
    orderSituationId: values.orderSituationId || undefined,
    generalClassId: values.generalClassId || undefined,
    sellingPrice: values.sellingPrice ?? undefined,
    paymentConditionId: values.paymentConditionId || undefined,
    shippingTypeId: values.shippingTypeId || undefined,
    trackingCode: values.trackingCode || undefined,
    shippingCost: centsDigitsToReais(values.shippingCost) || undefined,
    totalCost: cleanCurrency(values.totalCost) || undefined,
    /* Com frete: é contra este número que o backend confere a soma das parcelas. */
    total: (values.sellingPrice ?? cleanCurrency(values.totalCost) ?? 0)
      + (centsDigitsToReais(values.shippingCost) || 0),
    startDateOfDelivery: deliveryDateVal,
    finalDateOfDelivery: deliveryDateVal,
    orderDate: toDate(values.orderDate) || new Date().toISOString(),
    extraInfo: values.extraInfo || undefined,
    criticalAnalysis: values.criticalAnalysis || undefined,
    criticalAnalysisDesc: values.criticalAnalysisDesc || undefined,
    quantityItems: values.quantityItems || undefined,
    quantityPieces: values.quantityPieces || undefined,
    deliveryTime: values.deliveryTime || undefined,
    maximumBillingAmount: values.maximumBillingAmount || undefined,
    /** Vale só na criação — o PATCH de edição remove este campo antes de enviar. */
    saleStatus: 'Em Orçamento',
    fileIds,
    items: buildFashionItemsPayload(items),
    paymentMethods: values.paymentMethodId ? [{ paymentMethodId: values.paymentMethodId }] : [],
    installments: values.installments.map(i => ({
      numero: i.numero,
      valor: i.valor,
      vencimento: i.vencimento,
      diasOffset: i.diasOffset || undefined,
      comissaoAtiva: i.comissaoAtiva || undefined,
    })),
  }

  return payload
}

export const uploadFileToGeneralFiles = async (file: File): Promise<number> => {
  const uploaded = await AttachmentApiService.upload(file)
  return uploaded.id
}
